import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export type FooterFakeProgressBarProps = {
  hls: Hls;
  duration: number;
  color?: string;
};

function getProgressCurve(t: number, total: number) {
  const a = 0.7;
  return Math.log(1 + a * t) / Math.log(1 + a * total);
}

const FooterFakeProgressBar = ({ hls, duration, color }: FooterFakeProgressBarProps) => {
  const [progress, setProgress] = useState(0); // 0 a 1
  const [ended, setEnded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const intervalRef = useRef<any>(null);
  const startTimeRef = useRef<number>(0);
  const lastElapsedRef = useRef<number>(0);
  const fake80Time = 25; // 25 segundos para chegar a 80%
  const fake90Time = 85; // 85 segundos para chegar a 90% (25 + 60)
  const [reached80, setReached80] = useState(false);
  const [reached90, setReached90] = useState(false);
  const [real90Time, setReal90Time] = useState<number | null>(null);

  useEffect(() => {
    if (!playing || ended) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      return;
    }
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const elapsed = ((Date.now() - startTimeRef.current) / 1000) + lastElapsedRef.current;

      // Fase 1: 0% a 80% - velocidade rápida
      if (elapsed < fake80Time) {
        const newProgress = Math.min((elapsed / fake80Time) * 0.8, 0.8);
        setProgress(newProgress);
        setReached80(false);
        setReached90(false);
        console.log(`Fase 1: ${elapsed.toFixed(1)}s -> ${(newProgress * 100).toFixed(1)}%`);
      }
      // Fase 2: 80% a 90% - velocidade diminuindo progressivamente (1 minuto)
      else if (elapsed < fake90Time) {
        setReached80(true);
        setReached90(false);
        const phase2Elapsed = elapsed - fake80Time;
        const phase2Duration = fake90Time - fake80Time; // 60 segundos

        // Calcula o progresso da fase 2 usando uma curva de desaceleração
        // A velocidade diminui progressivamente, mas sempre avança para frente
        const phase2Progress = phase2Elapsed / phase2Duration;

        // Usa uma função que garante progresso sempre crescente
        // Começa rápido e diminui, mas nunca para ou volta
        const decelerationCurve = Math.pow(phase2Progress, 0.5); // Raiz quadrada para desaceleração suave
        const newProgress = 0.8 + (decelerationCurve * 0.1);

        setProgress(newProgress);
        console.log(`Fase 2: ${elapsed.toFixed(1)}s, phase2Elapsed: ${phase2Elapsed.toFixed(1)}s, decelerationCurve: ${decelerationCurve.toFixed(3)}, progress: ${(newProgress * 100).toFixed(1)}%`);
      }
      // Fase 3: 90% a 100% - usa o tempo real restante do vídeo
      else {
        setReached80(true);
        setReached90(true);

        // Captura o tempo real quando atinge 90%
        if (real90Time === null && hls && hls.media) {
          setReal90Time(hls.media.currentTime);
        }

        if (hls && hls.media && real90Time !== null && duration > real90Time) {
          const current = hls.media.currentTime;
          const remainingTime = duration - real90Time;
          const elapsedSince90 = current - real90Time;

          // Calcula o progresso baseado no tempo real restante
          const realProgress = Math.min(elapsedSince90 / remainingTime, 1);
          const newProgress = 0.9 + (realProgress * 0.1);
          setProgress(newProgress);
          console.log(`Fase 3: realTime: ${current.toFixed(1)}s, progress: ${(newProgress * 100).toFixed(1)}%`);
        } else {
          // Fallback: se não conseguir usar o tempo real, usa uma estimativa
          const phase3Elapsed = elapsed - fake90Time;
          const estimatedPhase3Duration = 15; // 15 segundos para os últimos 10%
          const phase3Progress = Math.min(phase3Elapsed / estimatedPhase3Duration, 1);
          const newProgress = 0.9 + (phase3Progress * 0.1);

          setProgress(newProgress);
          console.log(`Fase 3 (fallback): ${elapsed.toFixed(1)}s, progress: ${(newProgress * 100).toFixed(1)}%`);
        }
      }
    }, 50);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing, ended, fake80Time, fake90Time, real90Time, duration, hls]);

  useEffect(() => {
    if (!hls || !hls.media) return;
    const video = hls.media;
    const onPlaying = () => {
      setPlaying(true);
      setEnded(false);
      startTimeRef.current = Date.now();
    };
    const onPause = () => {
      setPlaying(false);
      lastElapsedRef.current += (Date.now() - startTimeRef.current) / 1000;
    };
    const onWaiting = () => {
      setPlaying(false);
      lastElapsedRef.current += (Date.now() - startTimeRef.current) / 1000;
    };
    const onEnded = () => {
      setEnded(true);
      setPlaying(false);
      setProgress(1);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
    const onSeeked = () => {
      if (video.currentTime === 0) {
        setEnded(false);
        setProgress(0);
        lastElapsedRef.current = 0;
        startTimeRef.current = Date.now();
        setReached80(false);
        setReached90(false);
        setReal90Time(null);
      }
    };
    video.addEventListener('playing', onPlaying);
    video.addEventListener('pause', onPause);
    video.addEventListener('waiting', onWaiting);
    video.addEventListener('ended', onEnded);
    video.addEventListener('seeked', onSeeked);
    return () => {
      video.removeEventListener('playing', onPlaying);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('waiting', onWaiting);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('seeked', onSeeked);
    };
  }, [hls, duration]);

  // Visual: barra simples preenchendo de acordo com o progresso
  return (
    <div className="smartplayer__footerFakeProgressBar" style={{ background: 'rgba(255,255,255,0.15)' }}>
      <div style={{ width: `${Math.min(progress * 100, 100)}%`, height: '100%', background: color || 'var(--primary-color, #444CE7)', transition: 'width 0.2s linear' }} />
    </div>
  );
};

export default FooterFakeProgressBar;
